import { useCallback, useEffect, useState } from "react";

import { cachedUserId, generateClientId, offlineDb } from "@/lib/offline/db";
import { syncService, type SyncStatus } from "@/lib/offline/syncService";

interface UseOfflineIncidentSignoffResult {
  signOff: (notes: string) => Promise<void>;
  isPending: boolean;
  syncStatus: SyncStatus;
}

/**
 * Enqueue an incident sign-off that works whether we're online or offline
 * (NAU-219d). Submit flow:
 *
 *   1. Persist the resolution notes + `pending` state to `incidentSignoffs`,
 *      keyed by incidentId so accidental double-submits are deduped.
 *   2. Drop the matching row from the `openIncidents` cache so the dashboard
 *      rail and the FridgeTempPanel badge stop showing it immediately.
 *   3. Append an `incidentSignoff` worker to the sync queue (priority 4 —
 *      higher than photos / finalize so it lands before related followups).
 *   4. If online, kick an immediate sync so the manager sees the round-trip
 *      complete instead of waiting for the debounce.
 */
export function useOfflineIncidentSignoff(
  incidentId: string,
): UseOfflineIncidentSignoffResult {
  const [isPending, setIsPending] = useState(false);
  const [syncStatus, setSyncStatus] = useState<SyncStatus>({
    pendingCount: 0,
    errorCount: 0,
    isSyncing: false,
    isOnline: typeof navigator !== "undefined" ? navigator.onLine : true,
  });

  useEffect(() => {
    syncService.getStatus().then(setSyncStatus);
    const unsubscribe = syncService.subscribe(setSyncStatus);
    return () => {
      unsubscribe();
    };
  }, []);

  const signOff = useCallback(
    async (notes: string) => {
      setIsPending(true);
      try {
        const trimmed = notes.trim();
        await offlineDb.incidentSignoffs.put({
          id: incidentId,
          resolutionNotes: trimmed.length > 0 ? trimmed : null,
          queuedAt: new Date().toISOString(),
          capturedByUserId: await cachedUserId(),
          syncState: "pending",
        });

        // Optimistically remove from the open-incidents cache so the dashboard
        // updates instantly. The server roundtrip + dashboard refetch will
        // confirm; if the server returns 409 (already resolved), nothing to do.
        await offlineDb.openIncidents.delete(incidentId);

        // Avoid duplicate queue entries if the manager clicked twice while
        // offline — bail out early.
        const alreadyQueued = await offlineDb.syncQueue
          .filter(
            (i) => i.type === "incidentSignoff" && i.entityId === incidentId,
          )
          .count();
        if (alreadyQueued === 0) {
          await offlineDb.syncQueue.add({
            id: generateClientId(),
            type: "incidentSignoff",
            entityId: incidentId,
            priority: 4,
            createdAt: Date.now(),
            attempts: 0,
          });
        }

        if (navigator.onLine) {
          await syncService.immediateSync();
        }
      } finally {
        setIsPending(false);
      }
    },
    [incidentId],
  );

  return { signOff, isPending, syncStatus };
}
